import React, { useState, useEffect } from 'react';
import { GripVertical, ArrowUp, ArrowDown } from 'lucide-react';

const QuestionCard = ({ question, selectedOptionId, onSelect }) => {
    const [rankedOptions, setRankedOptions] = useState([]);
    const [draggedIndex, setDraggedIndex] = useState(null);

    // Initialize ranked options when question changes
    useEffect(() => {
        if (question.type === 'rank-order') {
            // If we have a saved answer (array of IDs), restore that order.
            if (Array.isArray(selectedOptionId) && selectedOptionId.length > 0) {
                const ordered = selectedOptionId
                    .map(id => question.options.find(o => o.id === id))
                    .filter(o => !!o);
                setRankedOptions(ordered);
            } else {
                // Default order
                setRankedOptions([...question.options]);
            }
        }
    }, [question, selectedOptionId]);

    const handleDragStart = (e, index) => {
        setDraggedIndex(index);
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', index.toString());

        // Create a custom drag image to avoid the "ghost" effect being too large/distracting
        // or just let default behavior happen but style the original element
    };

    const handleDragOver = (e, index) => {
        e.preventDefault();
        if (draggedIndex === null || draggedIndex === index) return;

        const newItems = [...rankedOptions];
        const draggedItem = newItems[draggedIndex];

        // Remove dragged item
        newItems.splice(draggedIndex, 1);
        // Insert at new position
        newItems.splice(index, 0, draggedItem);

        setRankedOptions(newItems);
        setDraggedIndex(index);
    };

    const handleDragEnd = () => {
        setDraggedIndex(null);
        // Trigger update on drag end to save state
        const orderedIds = rankedOptions.map(o => o.id);
        onSelect(question.id, orderedIds);
    };

    const moveItem = (index, direction) => {
        if (direction === 'up' && index === 0) return;
        if (direction === 'down' && index === rankedOptions.length - 1) return;

        const newItems = [...rankedOptions];
        const targetIndex = direction === 'up' ? index - 1 : index + 1;

        [newItems[index], newItems[targetIndex]] = [newItems[targetIndex], newItems[index]];
        setRankedOptions(newItems);

        // Trigger update
        const orderedIds = newItems.map(o => o.id);
        onSelect(question.id, orderedIds);
    };

    const getRankStyles = (index) => {
        switch (index) {
            case 0: return { color: 'text-emerald-400', bg: 'bg-emerald-900/30', border: 'border-emerald-500/50' };
            case 1: return { color: 'text-lime-400', bg: 'bg-lime-900/30', border: 'border-lime-500/50' };
            case 2: return { color: 'text-orange-400', bg: 'bg-orange-900/30', border: 'border-orange-500/50' };
            case 3: return { color: 'text-rose-400', bg: 'bg-rose-900/30', border: 'border-rose-500/50' };
            default: return { color: 'text-slate-400', bg: 'bg-slate-800', border: 'border-slate-700' };
        }
    };

    const renderRankingContent = () => (
        <div className="space-y-6">
            <div className="p-4 rounded-lg text-sm mb-4 border flex gap-2" style={{ background: "rgba(59, 130, 246, 0.1)", borderColor: "rgba(59, 130, 246, 0.3)", color: "#93C5FD" }}>
                <ArrowUp className="w-5 h-5 flex-shrink-0" />
                <div>
                    <strong className="text-blue-300">Instructions:</strong> Drag the items to rank them.
                    <br />
                    Top (<span className="text-emerald-400 font-bold">1</span>) is most like you. Bottom (<span className="text-rose-400 font-bold">4</span>) is least like you.
                </div>
            </div>

            <div className="space-y-3 relative">
                {rankedOptions.map((option, index) => {
                    const styles = getRankStyles(index);
                    return (
                        <div
                            key={option.id}
                            draggable
                            onDragStart={(e) => handleDragStart(e, index)}
                            onDragOver={(e) => handleDragOver(e, index)}
                            onDragEnd={handleDragEnd}
                            className={`flex items-center p-3 sm:p-4 border-2 rounded-xl shadow-sm cursor-grab active:cursor-grabbing transition-all ${draggedIndex === index
                                    ? 'border-primary-purple opacity-50 scale-95 z-20 bg-slate-800'
                                    : 'border-slate-700 hover:border-primary-purple/50 z-10 bg-slate-800/50'
                                }`}
                            style={{ backdropFilter: "blur(10px)" }}
                        >
                            <div className="mr-3 text-slate-500 cursor-grab">
                                <GripVertical className="w-5 h-5" />
                            </div>

                            <div className="mr-3 flex flex-col gap-1 md:hidden">
                                <button
                                    onClick={() => moveItem(index, 'up')}
                                    disabled={index === 0}
                                    className="p-1 bg-slate-700 rounded disabled:opacity-30 text-slate-300"
                                >
                                    <ArrowUp className="w-4 h-4" />
                                </button>
                                <button
                                    onClick={() => moveItem(index, 'down')}
                                    disabled={index === rankedOptions.length - 1}
                                    className="p-1 bg-slate-700 rounded disabled:opacity-30 text-slate-300"
                                >
                                    <ArrowDown className="w-4 h-4" />
                                </button>
                            </div>

                            <div className="flex-grow text-sm sm:text-base font-medium text-slate-200 leading-snug">
                                {option.text}
                            </div>

                            <div className={`ml-3 sm:ml-4 flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center font-bold text-lg border-2 transition-colors ${styles.bg} ${styles.color} ${styles.border}`}>
                                {index + 1}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );

    return (
        <div className="w-full max-w-2xl mx-auto p-6 rounded-2xl shadow-lg border border-slate-700 fade-in" style={{ background: "rgba(15, 23, 42, 0.6)" }}>
            <h3 className="text-xl font-semibold text-slate-100 mb-6 leading-relaxed">
                <span className="text-primary-purple mr-2">{question.index + 1}.</span>
                {question.text}
            </h3>
            {renderRankingContent()}
        </div>
    );
};

export default QuestionCard;
